/**
 * ContactStep — name + email + phone + SMS opt-in fields. The four
 * pieces of information we need to identify the customer and reach
 * them about their appointment.
 *
 * Lifted state: parent owns each field's useState so the booking
 * page can ship them to the API on submit. This component is purely
 * presentational — no fetches, no validation. Parent does the
 * required-fields check at submit time.
 *
 * Notes textarea is intentionally NOT included here. Notes describe
 * the appointment, not the contact — and /book already has its own
 * notes section. Keeps responsibilities clean.
 *
 * Extracted from consultation/page.tsx — Phase 1.2 of booking
 * consolidation (wiki/plans/booking-consolidation-2026-04-28.md).
 * Behavior preserved verbatim: same i18n keys, same Input component,
 * same SMS-consent checkbox + link, same "/sms-terms" target blank.
 */
"use client";

import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/Input";

interface ContactStepProps {
  name: string;
  setName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  smsOptIn: boolean;
  setSmsOptIn: (v: boolean) => void;
  /**
   * i18n namespace to read keys from. Defaults to "consultation" so
   * existing translation files keep working unchanged. Phase 2 of
   * the consolidation will move keys to a shared "booking" namespace.
   */
  i18nNamespace?: string;
}

export default function ContactStep({
  name,
  setName,
  email,
  setEmail,
  phone,
  setPhone,
  smsOptIn,
  setSmsOptIn,
  i18nNamespace = "consultation",
}: ContactStepProps) {
  const t = useTranslations(i18nNamespace);
  return (
    <>
      <Input
        label={t("fullName")}
        placeholder={t("namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Input
        label={t("email")}
        type="email"
        placeholder={t("emailPlaceholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Input
        label={t("phoneNumber")}
        type="tel"
        placeholder={t("phonePlaceholder")}
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
      />
      <label className="flex items-start gap-2.5 -mt-4 cursor-pointer">
        <input
          type="checkbox"
          checked={smsOptIn}
          onChange={(e) => setSmsOptIn(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-gold focus:ring-brand-gold/50 shrink-0"
        />
        <span className="text-[11px] text-brand-muted leading-relaxed">
          {t("smsConsent")}{" "}
          <a
            href="/sms-terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-gold hover:underline"
          >
            {t("smsTermsLink")}
          </a>
        </span>
      </label>
    </>
  );
}
